import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { LoadingButton } from "@mui/lab";
import FileUploader from "app/widgets/Shared/FileUploader/FileUploader";
import erpInventoryServices from "app/services/erp-inventory-services";
import { downloadBuffer } from "app/utils/appHelpers";
import { useSnackbar } from "notistack";
import moment from "moment";

// Carga de stock por planilla, para los clientes sin ERP. Una bodega por
// archivo: la bodega se elige acá y no va como columna, porque un nombre mal
// escrito en una fila sería un producto cargado en ninguna parte.
const InventoryImportModal = ({ open = false, onClose = () => {}, centres = [], defaultCentreId = "", onImported = () => {} }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [centreId, setCentreId] = useState("");
    const [filesToImport, setFilesToImport] = useState([]);
    const [importing, setImporting] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [importErrors, setImportErrors] = useState([]);

    // Parte en la bodega que se está mirando: es casi siempre la que se va a cargar.
    useEffect(() => {
        if (!open) return;
        const fallback = centres.length === 1 ? centres[0].id : "";
        const matches = centres.some((centre) => String(centre.id) === String(defaultCentreId));
        setCentreId(matches ? defaultCentreId : fallback);
    }, [open, centres, defaultCentreId]);

    const handleClose = () => {
        setFilesToImport([]);
        setImporting(false);
        setImportErrors([]);
        onClose();
    };

    const downloadTemplate = async () => {
        try {
            setDownloadingTemplate(true);
            const data = await erpInventoryServices.downloadTemplate();
            downloadBuffer(data, `inventario_template_${moment().format("YYYYMMDDHHmmss")}.xlsx`);
        } catch (_error) {
            // El interceptor muestra el mensaje genérico.
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const importFile = async () => {
        if (!filesToImport.length || !centreId) return;

        try {
            setImporting(true);
            setImportErrors([]);

            const response = await erpInventoryServices.importInventory(centreId, filesToImport[0]);
            enqueueSnackbar(response.message || "Importación completada", { variant: "success" });
            onImported();
            handleClose();
        } catch (error) {
            const details = error?.response?.data?.details;
            if (Array.isArray(details) && details.length > 0) {
                setImportErrors(details);
            } else {
                enqueueSnackbar(error?.response?.data?.error || "No se pudo importar", { variant: "error" });
            }
        } finally {
            setImporting(false);
        }
    };

    return (
        <Dialog fullWidth open={open} onClose={handleClose}>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h2" color="primary">
                        Importar stock
                    </Typography>
                    <LoadingButton
                        loading={downloadingTemplate}
                        variant="contained"
                        color="success"
                        size="small"
                        endIcon={<DownloadOutlinedIcon />}
                        disabled={downloadingTemplate}
                        loadingPosition="end"
                        onClick={downloadTemplate}
                    >
                        {downloadingTemplate ? "Descargando" : "Ejemplo"}
                    </LoadingButton>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Las columnas obligatorias son Código y Stock; Nombre, Código de barra y Unidad son opcionales.
                    Lo que ya estaba cargado se actualiza y lo que no viene en la planilla queda como estaba: para
                    dejar un producto sin stock, ponle 0. Los productos que sincroniza el ERP no se pueden cargar acá.
                </Typography>

                <TextField select fullWidth size="small" label="Bodega" value={centreId} sx={{ mb: 2, mt: 1 }}
                           onChange={(event) => setCentreId(event.target.value)}>
                    {centres.map((centre) => (
                        <MenuItem key={centre.id} value={centre.id}>{centre.name}</MenuItem>
                    ))}
                </TextField>

                <FileUploader acceptFamily="spreadsheet" maxFiles={1} onFileSelection={setFilesToImport} />

                {importErrors.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Errores detectados. No se cargó nada: corrige el archivo y vuelve a subirlo.
                        </Typography>
                        <List disablePadding>
                            {importErrors.map((errorMessage, index) => (
                                <ListItem key={`inventory-import-error-${index}`} sx={{ py: 0 }}>
                                    <Typography variant="body2">- {errorMessage}</Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cerrar</Button>
                <LoadingButton
                    loading={importing}
                    variant="contained"
                    color="success"
                    size="small"
                    endIcon={<SaveOutlinedIcon />}
                    disabled={!filesToImport.length || !centreId}
                    loadingPosition="end"
                    onClick={importFile}
                >
                    {importing ? "Importando" : "Importar"}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default InventoryImportModal;
